import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Capteur, CapteurPayload, CapteursService } from '../../../services/capteurs.service';
import { Nomenclature, NomenclaturesService } from '../../../services/nomenclatures.service';
import { CommandesService } from '../../../services/commandes.service';
import { StatutCapteursService, StatutCapteur } from '../../../services/statut-capteurs.service';
import { Cartouche, CartouchesService } from '../../../services/cartouches.service';
import { LotCartouche, LotsCartouchesService } from '../../../services/lots-cartouches.service';
import { TypeCapteur, TypeCapteursService } from '../../../services/type-capteurs.service';

@Component({
  selector: 'app-creer-capteur',
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatOptionModule],
  templateUrl: './creer-capteur.component.html',
  styleUrl: './creer-capteur.component.scss'
})

export class CreerCapteurComponent implements OnInit {
  protected capteurs: Capteur[] = [];
  protected repriseCapteurId = '';
  lotCartoucheAName: string = '';
  lotCartoucheBName: string = '';
  typeCapteurs: TypeCapteur[] = [];
  typeCapteur = '';
  statutCapteurId: number | null = null;
  statutsCapteurs: StatutCapteur[] = [];
  cartoucheAInput: string = '';
  cartoucheBInput: string = '';
  filteredCartouchesA: Cartouche[] = [];
  filteredCartouchesB: Cartouche[] = [];
  cartoucheAId: number | null = null;
  cartoucheBId: number | null = null;
  lignesCommandeEnPreparation: any[] = [];
  selectedLigneCommandeId: number | null = null;
  protected nomenclatures: Nomenclature[] = [];
  protected lotsCartouches: LotCartouche[] = [];

  protected numeroSerie = '';
  protected dateAssemblage = '';
  protected numeroNomenclature = '';
  protected dateEmballage = '';
  protected codeBonRetour = '';
  protected codeBonRetourCartouches = '';
  protected dateEnvoi = '';
  protected dateExpeditionCapteur = '';
  protected referenceEnvoi = '';
  protected dateRetour = '';
  protected nombreHeuresFonctionnement = '';
  protected nombreLitresAspires = '';
  protected commentaire = '';
  protected macCarteElectronique = '';
  protected loadingCapteurs = false;

  protected loading = false;
  protected loadingNomenclatures = false;
  protected success = '';
  protected error = '';

  private numeroSerieLookupToken = 0;

  private normalizeNombreHeuresFonctionnement(value: string): string | undefined | null {
    const normalized = value.trim();
    if (!normalized) {
      return undefined;
    }
    if (!/^\d{3}h\d{2}$/.test(normalized)) {
      this.error = "Le nombre d'heures de fonctionnement doit respecter le format 008h12.";
      return null;
    }
    return normalized;
  }

  constructor(
    private readonly capteurService: CapteursService,
    private readonly nomenclaturesService: NomenclaturesService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly commandesService: CommandesService,
    private readonly statutCapteursService: StatutCapteursService,
    private readonly cartouchesService: CartouchesService,
    private readonly lotsService: LotsCartouchesService,
    private readonly typeCapteursService: TypeCapteursService
  ) {}

  ngOnInit(): void {
    this.loadCapteurs();
    this.loadNomenclatures();
    this.loadLots();
    this.loadTypeCapteurs();
    this.loadStatutsCapteurs();
    this.loadLignesCommandeEnPreparation();
  }

  protected onNumeroSerieChange(value: string): void {
    this.numeroSerie = value;
    this.cdr.detectChanges();
    const trimmedValue = value.trim();
    if (!/^[123]$/.test(trimmedValue)) {
      this.numeroSerieLookupToken += 1;
      return;
    }

    const requestToken = ++this.numeroSerieLookupToken;
    this.capteurService.getNextNumeroSerie(trimmedValue).subscribe({
      next: (response) => {
        if (requestToken !== this.numeroSerieLookupToken) {
          return;
        }

        if (this.numeroSerie.trim() !== trimmedValue) {
          return;
        }

        this.numeroSerie = response.numero_serie;
        this.cdr.detectChanges();
      }
    });
  }

  protected onReprendreCapteurChange(): void {
    const selected = this.capteurs.find((capteur) => capteur.id === Number(this.repriseCapteurId));
    if (!selected) {
      return;
    }

    this.typeCapteur = selected.type_capteur || '';
    this.numeroSerie = selected.numero_serie || '';
    this.dateAssemblage = selected.date_assemblage || '';
    this.numeroNomenclature = selected.numero_nomenclature != null ? String(selected.numero_nomenclature) : '';
    this.selectedLigneCommandeId = selected.ligne_commande_id ?? null;
    this.nombreHeuresFonctionnement = selected.nombre_heures_fonctionnement ?? '';
    this.nombreLitresAspires = selected.nombre_litres_aspires != null ? String(selected.nombre_litres_aspires) : '';
    this.commentaire = selected.commentaire || '';
    this.macCarteElectronique = selected.code_mac_carte_electronique || '';
    this.statutCapteurId = selected.statut_capteur_id ?? null;
    this.cartoucheAId = null;
    this.cartoucheBId = null;
    this.cartoucheAInput = '';
    this.cartoucheBInput = '';
    this.lotCartoucheAName = '';
    this.lotCartoucheBName = '';
    this.dateEmballage = '';
    this.codeBonRetour = '';
    this.codeBonRetourCartouches = '';
    this.dateEnvoi = '';
    this.dateExpeditionCapteur = '';
    this.referenceEnvoi = '';
    this.dateRetour = '';
  }

  private loadTypeCapteurs(): void {
    this.typeCapteursService.list().subscribe({
      next: (types: TypeCapteur[]) => {
        this.typeCapteurs = types;
        this.cdr.markForCheck();
      },
      error: () => {
        this.typeCapteurs = [];
        this.cdr.markForCheck();
      }
    });
  }

  onCartoucheAInputChange(value: string): void {
    const trimmedValue = value ? value.trim() : '';

    if (!trimmedValue) {
      this.cartoucheAId = null;
      this.lotCartoucheAName = '';
    }
    
    if (trimmedValue.length >= 1) {
      // Recherche avec au moins 1 caractère
      this.cartouchesService.autocompleteNom(trimmedValue).subscribe((cartouches: Cartouche[]) => {
        this.filteredCartouchesA = cartouches;
        const exactMatch = cartouches.find((cartouche: Cartouche) => (cartouche.nom ?? '') === trimmedValue);
        if (exactMatch) {
          this.cartoucheAId = exactMatch.id;
          this.lotCartoucheAName = this.getLotName(exactMatch.lot_cartouche_id);
        } else {
          this.cartoucheAId = null;
          this.lotCartoucheAName = '';
        }
        this.cdr.markForCheck();
      });
    } else if (trimmedValue.length === 0) {
      // Champ vide, afficher les 10 premiers
      this.cartouchesService.autocompleteNom('').subscribe((cartouches: Cartouche[]) => {
        this.filteredCartouchesA = cartouches;
        this.cdr.markForCheck();
      });
    }
  }

  onCartoucheASelected(event: any): void {
    const cartouche = event.option.value;
    this.cartoucheAInput = cartouche.nom;
    this.cartoucheAId = cartouche.id;
    this.lotCartoucheAName = this.getLotName(cartouche.lot_cartouche_id);
    this.cdr.markForCheck();
  }

  onCartoucheBInputChange(value: string): void {
    const trimmedValue = value ? value.trim() : '';

    if (!trimmedValue) {
      this.cartoucheBId = null;
      this.lotCartoucheBName = '';
    }
    
    if (trimmedValue.length >= 1) {
      // Recherche avec au moins 1 caractère
      this.cartouchesService.autocompleteNom(trimmedValue).subscribe((cartouches: Cartouche[]) => {
        this.filteredCartouchesB = cartouches;
        const exactMatch = cartouches.find((cartouche: Cartouche) => (cartouche.nom ?? '') === trimmedValue);
        if (exactMatch) {
          this.cartoucheBId = exactMatch.id;
          this.lotCartoucheBName = this.getLotName(exactMatch.lot_cartouche_id);
        } else {
          this.cartoucheBId = null;
          this.lotCartoucheBName = '';
        }
        this.cdr.markForCheck();
      });
    } else if (trimmedValue.length === 0) {
      // Champ vide, afficher les 10 premiers
      this.cartouchesService.autocompleteNom('').subscribe((cartouches: Cartouche[]) => {
        this.filteredCartouchesB = cartouches;
        this.cdr.markForCheck();
      });
    }
  }

  onCartoucheBSelected(event: any): void {
    const cartouche = event.option.value;
    this.cartoucheBInput = cartouche.nom;
    this.cartoucheBId = cartouche.id;
    this.lotCartoucheBName = this.getLotName(cartouche.lot_cartouche_id);
    this.cdr.markForCheck();
  }

  displayCartouche(value: Cartouche | string | null): string {
    if (!value) {
      return '';
    }

    return typeof value === 'string' ? value : (value.nom ?? '');
  }

  getLotName(lotId?: number): string {
    if (!lotId) return '';
    const lot = this.lotsCartouches.find((l) => l.id === lotId);
    return lot ? lot.nom : '';
  }

  private loadLignesCommandeEnPreparation(): void {
    this.commandesService.listLignesEnCoursDePreparation().subscribe({
      next: (response) => {
        const items = response?.items ?? [];
        this.lignesCommandeEnPreparation = items.map((ligne) => ({
          ...ligne,
          _affichage: `${ligne.numero_commande} (Ligne #${ligne.id})${ligne.type_capteur ? ' - ' + ligne.type_capteur : ''}${(ligne.nom_cartouche_a || ligne.nom_cartouche_b) ? ' - ' + [ligne.nom_cartouche_a, ligne.nom_cartouche_b].filter(Boolean).join(' / ') : ''}${ligne.date_expedition ? ' - ' + this.formatDateFr(ligne.date_expedition) : ''}`
        }));
        this.cdr.markForCheck();
      },
      error: () => {
        this.lignesCommandeEnPreparation = [];
        this.cdr.markForCheck();
      }
    });
  }

  protected onLigneCommandeChange(): void {
    if (!this.selectedLigneCommandeId || !this.typeCapteur) {
      return;
    }

    const selectedLigne = this.lignesCommandeEnPreparation.find(
      (ligne) => ligne.id === this.selectedLigneCommandeId
    );
    if (!selectedLigne?.type_capteur) {
      return;
    }

    const ligneType = this.normalizeTypeCapteur(selectedLigne.type_capteur);
    const capteurType = this.normalizeTypeCapteur(this.typeCapteur);
    if (ligneType && capteurType && ligneType !== capteurType) {
      alert(
        `Attention: la ligne de commande est de type "${selectedLigne.type_capteur}" alors que le capteur est de type "${this.typeCapteur}".`
      );
    }
  }

  protected onTypeCapteurChange(): void {
    if (this.isSingleCartoucheType()) {
      this.clearCartoucheBSelection();
    }
    this.onLigneCommandeChange();
  }

  protected isSingleCartoucheType(): boolean {
    return this.normalizeTypeCapteur(this.typeCapteur).startsWith('1 cartouche');
  }

  protected getCartoucheALabel(): string {
    return this.isSingleCartoucheType() ? 'Cartouche' : 'Cartouche A';
  }

  protected getLotCartoucheALabel(): string {
    return this.isSingleCartoucheType() ? 'Lot cartouche' : 'Lot cartouche A';
  }

  private clearCartoucheBSelection(): void {
    this.cartoucheBId = null;
    this.cartoucheBInput = '';
    this.lotCartoucheBName = '';
    this.filteredCartouchesB = [];
  }

  private normalizeTypeCapteur(value: string | null | undefined): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private formatDateFr(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch {
      return dateString;
    }
  }

  private loadStatutsCapteurs(): void {
    this.statutCapteursService.list().subscribe({
      next: (statuts) => {
        this.statutsCapteurs = statuts;
        this.applyDefaultStatutCapteur();
        this.cdr.markForCheck();
      },
      error: () => {
        this.statutsCapteurs = [];
        this.cdr.markForCheck();
      }
    });
  }

  protected isStatutEnCoursDeTransit(): boolean {
    const statutId = Number(this.statutCapteurId);
    if (!Number.isFinite(statutId) || statutId <= 0) {
      return false;
    }

    const statut = this.statutsCapteurs.find((s) => s.id === statutId);
    const nom = String(statut?.nom ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
    return nom === 'en cours de transit';
  }

  private applyDefaultStatutCapteur(): void {
    if (this.statutCapteurId !== null) {
      return;
    }

    const defaultStatut = this.statutsCapteurs.find((statut) => {
      const nom = String(statut.nom ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
      return nom === 'en production';
    });

    if (defaultStatut) {
      this.statutCapteurId = defaultStatut.id;
    }
  }



  private loadLots(): void {
    this.zone.run(() => {
      this.lotsService.list().subscribe({
        next: (lots: LotCartouche[]) => {
          this.lotsCartouches = lots;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lotsCartouches = [];
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected submit(): void {
    this.error = '';
    this.success = '';

    if (!this.numeroSerie.trim()) {
      this.error = 'Le numéro de série est requis.';
      return;
    }

    if (!this.numeroNomenclature) {
      this.error = 'Le numéro de nomenclature est requis.';
      return;
    }

    const nombreHeuresFonctionnement = this.normalizeNombreHeuresFonctionnement(this.nombreHeuresFonctionnement);
    if (nombreHeuresFonctionnement === null) {
      return;
    }

    const payload: CapteurPayload = {
      type_capteur: this.typeCapteur || undefined,
      numero_serie: this.numeroSerie.trim(),
      numero_nomenclature: Number(this.numeroNomenclature),
      date_assemblage: this.dateAssemblage || undefined,
      cartouche_a: this.cartoucheAId ?? undefined,
      cartouche_b: this.isSingleCartoucheType() ? null : (this.cartoucheBId ?? undefined),
      ligne_commande_id: this.selectedLigneCommandeId ?? undefined,
      date_emballage: this.dateEmballage || undefined,
      code_bon_retour: this.codeBonRetour || undefined,
      code_bon_retour_cartouches: this.codeBonRetourCartouches || undefined,
      date_envoi: this.dateEnvoi || undefined,
      date_expedition: this.dateExpeditionCapteur || undefined,
      reference_envoi: this.referenceEnvoi || undefined,
      date_retour: this.dateRetour || undefined,
      nombre_heures_fonctionnement: nombreHeuresFonctionnement,
      nombre_litres_aspires: this.nombreLitresAspires ? Number(this.nombreLitresAspires) : undefined,
      commentaire: this.commentaire || undefined,
      code_mac_carte_electronique: this.macCarteElectronique || undefined,
      statut_capteur_id: this.statutCapteurId ?? undefined,
    };

    this.loading = true;
    this.zone.run(() => {
      this.capteurService.create(payload).subscribe({
        next: (created: Capteur) => {
          this.success = 'Capteur créé avec succès.';
          this.resetForm();
          this.capteurs = [created, ...this.capteurs.filter((capteur) => capteur.id !== created.id)];
          this.loadCapteurs();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private resetForm(): void {
    this.numeroSerie = '';
    this.typeCapteur = '';
    this.dateAssemblage = '';
    this.numeroNomenclature = '';
    this.cartoucheAInput = '';
    this.cartoucheBInput = '';
    this.cartoucheAId = null;
    this.cartoucheBId = null;
    this.filteredCartouchesA = [];
    this.filteredCartouchesB = [];
    this.lotCartoucheAName = '';
    this.lotCartoucheBName = '';
    this.selectedLigneCommandeId = null;
    this.dateEmballage = '';
    this.codeBonRetour = '';
    this.codeBonRetourCartouches = '';
    this.dateEnvoi = '';
    this.dateExpeditionCapteur = '';
    this.referenceEnvoi = '';
    this.dateRetour = '';
    this.nombreHeuresFonctionnement = '';
    this.nombreLitresAspires = '';
    this.commentaire = '';
    this.macCarteElectronique = '';
    this.statutCapteurId = null;
    this.applyDefaultStatutCapteur();
  }

  private loadNomenclatures(): void {
    this.loadingNomenclatures = true;
    this.zone.run(() => {
      this.nomenclaturesService.list().subscribe({
        next: (nomenclatures) => {
          this.nomenclatures = nomenclatures;
          this.loadingNomenclatures = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des nomenclatures: ${this.getErrorMessage(err)}`;
          this.loadingNomenclatures = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadCapteurs(): void {
    this.loadingCapteurs = true;
    this.zone.run(() => {
      this.capteurService.list().subscribe({
        next: (capteurs) => {
          this.capteurs = capteurs;
          this.loadingCapteurs = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.capteurs = [];
          this.loadingCapteurs = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    if (err.error && typeof err.error === 'object') {
      return JSON.stringify(err.error);
    }

    if (err.status === 0) {
      return 'Impossible de contacter le serveur';
    }

    if (err.statusText) {
      return `${err.status} - ${err.statusText}`;
    }

    return 'Erreur inconnue';
  }
}
